import { Navigate, useLocation } from "react-router-dom";
import { useTheme } from "styled-components";
import { colors } from "@permisso/permisso-components";
import { BaseScreen } from "../components/baseScreen/BaseScreen";
import { Header } from "../components/header/Header";
import { Avatar } from "../components/avatar/Avatar";
import { T } from "../components/i18n/Translation";
import { ErrorLayout } from "../components/errorLayout/ErrorLayout";
import { CenteredContainer } from "./onboarding/components/CenteredContainer";
import { TEST_IDS } from "../config/testIds";
import { isUnavailableStateFromCurrentPageLoad } from "../utils/getDocumentationAreaRemovalReason";

export enum DocumentationAreaUnavailableReason {
  USER_ACCESS_REMOVED = "USER_ACCESS_REMOVED",
  USER_LINK_REMOVED = "USER_LINK_REMOVED",
  ACTOR_REMOVED = "ACTOR_REMOVED",
}

export type DocumentationAreaUnavailableLocationState = {
  reason?: string;
  pageLoadId?: string;
} | null;

type NoticeCopy = { titleKey: string; descriptionKey: string };

const DEFAULT_COPY: NoticeCopy = {
  titleKey: "documentation_area.unavailable.title",
  descriptionKey: "documentation_area.unavailable.description",
};

const COPY_BY_REASON: Record<DocumentationAreaUnavailableReason, NoticeCopy> = {
  [DocumentationAreaUnavailableReason.USER_ACCESS_REMOVED]: {
    titleKey: "documentation_area.unavailable.user_access_removed.title",
    descriptionKey: "documentation_area.unavailable.user_access_removed.description",
  },
  [DocumentationAreaUnavailableReason.USER_LINK_REMOVED]: {
    titleKey: "documentation_area.unavailable.user_access_removed.title",
    descriptionKey: "documentation_area.unavailable.user_access_removed.description",
  },
  [DocumentationAreaUnavailableReason.ACTOR_REMOVED]: {
    titleKey: "documentation_area.unavailable.actor_removed.title",
    descriptionKey: "documentation_area.unavailable.actor_removed.description",
  },
};

const isKnownReason = (reason?: string): reason is DocumentationAreaUnavailableReason =>
  reason !== undefined && Object.prototype.hasOwnProperty.call(COPY_BY_REASON, reason);

const getCopy = (reason?: string): NoticeCopy =>
  isKnownReason(reason) ? COPY_BY_REASON[reason] : DEFAULT_COPY;

export const DocumentationAreaUnavailableScreen = () => {
  const { state, search } = useLocation() as {
    state: DocumentationAreaUnavailableLocationState;
    search: string;
  };
  const { companyName, removeCustomerLogo } = useTheme();
  const { titleKey, descriptionKey } = getCopy(state?.reason);

  // A refresh (or a direct visit) retries the fetch from the home route
  if (!isUnavailableStateFromCurrentPageLoad(state)) {
    return <Navigate replace to={{ pathname: "/", search }} />;
  }

  return (
    <BaseScreen
      backgroundColor={colors.grey0}
      header={<Header />}
      screenPadding="2"
      mobileScreenPadding="20px"
    >
      <CenteredContainer>
        <ErrorLayout
          top={!removeCustomerLogo ? <Avatar /> : null}
          heading={<T k="documentation_area.unavailable.screen_title" params={{ companyName }} />}
          title={<T k={titleKey} />}
          description={<T k={descriptionKey} />}
          dataTestId={TEST_IDS.documentationAreaUnavailable.screen}
          noticeDataTestId={TEST_IDS.documentationAreaUnavailable.notice}
        />
      </CenteredContainer>
    </BaseScreen>
  );
};
